import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { BranchCode } from 'api/risk';
import { TabConfig } from 'components/navigation/Tabs/useConfigTabs';
import { contractBillingTabsConfig } from './tabs/contractBillingTabsConfig';
import { contractsAPI } from 'services/swagger-api';
import { PaymentDto } from 'api/contract';

export interface ContractBillingContextState {
  branchCode: BranchCode;
  payments: PaymentDto[];
  /**
   * Type de quittance de l'échéancier, libellé par le back (« Comptant », « Terme »,
   * « Sinistre », « Résiliation »). Porté par la racine de la réponse, il vaut pour
   * l'ensemble des échéances et n'est donc pas répété sur chacune.
   */
  receiptType?: string;
  tabs: TabConfig[];
  contractId: string
}

// Type complet du contexte
export type ContractBillingContextType = ContractBillingContextState;

// Création du contexte
const ContractBillingContext = createContext<ContractBillingContextType | undefined>(undefined);

// Props du provider
interface ContractBillingProviderProps {
  children: ReactNode;
  branchCode: BranchCode;
  contractId: string;
}

// Provider du contexte
export const ContractBillingProvider: React.FC<ContractBillingProviderProps> = ({
  children,
  branchCode,
  contractId
}) => {
  const [payments, setPayments] = useState<PaymentDto[]>([])
  const [receiptType, setReceiptType] = useState<string | undefined>()

  useEffect(() => {
    const getPayments = async () => {
      const response = await contractsAPI.getPayments({ contractId })
      if (response) {
        setPayments(response.payments)
        setReceiptType(response.receiptType ?? undefined)
      }
    }

    getPayments()
  }, [contractId])

  // Valeur du contexte
  const contextValue: ContractBillingContextType = {
    payments,
    receiptType,
    branchCode,
    tabs: contractBillingTabsConfig,
    contractId
  };

  return (
    <ContractBillingContext.Provider value={contextValue}>
      {children}
    </ContractBillingContext.Provider>
  );
};

export const useContractBilling = (): ContractBillingContextType => {
  const context = useContext(ContractBillingContext);
  if (context === undefined) {
    throw new Error('useContractBilling must be used within a ContractBillingProvider');
  }
  return context;
};

export const useContractBillingState = (): ContractBillingContextState => {
  const { branchCode, tabs, contractId, payments } = useContractBilling();
  return {
    payments,
    branchCode,
    tabs,
    contractId
  };
};
